// Ouverture du sélecteur de fichiers natif.
//
// Un `<input type="file">` détaché du document ne déclenche plus le sélecteur
// sur les Chrome récents : `.click()` est ignoré en silence, sans exception ni
// message de console. C'est la même restriction que `showPicker()`, qui lève
// déjà « InvalidStateError » sur un élément hors document ; Chrome l'a
// progressivement étendue à `.click()`.
//
// Constaté en prod le 16/09/2026 sur le bouton « Galerie » de la modale vidéo
// élève : le taux de ré-appui dans la minute suit la version de Chrome Android
// (150 : 23 %, 151 : 38 %, 152 : 47 %, 153 : 63 %) alors qu'iOS Safari reste
// plat autour de 25 %. Un même élève, sur le même téléphone, est passé de 7 %
// en Chrome 150 à 71 % en Chrome 152 : il appuyait, rien ne s'ouvrait.
//
// Le champ doit donc être DANS le document au moment du clic. On le rend hors
// écran plutôt qu'en `display:none` ou `hidden` : un champ non rendu est lui
// aussi susceptible d'être refusé.

/**
 * Crée un `<input type="file">` attaché au document, prêt à être cliqué.
 * L'appelant garde la référence et appelle `detachFileInput` quand il a fini.
 *
 * `capture` ('environment' | 'user') ouvre directement l'appareil photo du
 * téléphone au lieu de la galerie ; ignoré sur ordinateur.
 *
 * @param {{ accept?: string, capture?: string }} [options]
 * @returns {HTMLInputElement}
 */
export function createAttachedFileInput({ accept = 'video/*', capture } = {}) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = accept;
  if (capture) input.setAttribute('capture', capture);

  // Invisible mais rendu, et hors de portée du clavier comme du pointeur.
  input.style.position = 'fixed';
  input.style.top = '0';
  input.style.left = '0';
  input.style.width = '1px';
  input.style.height = '1px';
  input.style.opacity = '0';
  input.style.pointerEvents = 'none';
  input.style.zIndex = '-1';
  input.tabIndex = -1;
  input.setAttribute('aria-hidden', 'true');

  document.body.appendChild(input);
  return input;
}

/**
 * Retire du document un champ créé par `createAttachedFileInput` et coupe son
 * gestionnaire. Sans effet si `input` est absent ou déjà retiré.
 *
 * @param {HTMLInputElement | null | undefined} input
 */
export function detachFileInput(input) {
  if (!input) return;
  input.onchange = null;
  if (input.parentNode) input.parentNode.removeChild(input);
}
